import {
  BOARD_DRILL_COLUMNS,
  BOARD_DRILL_TABLE,
  DRILL_MAX_PAGES,
  DRILL_PAGE_SIZE,
} from "./boardDrill.mjs";
import type { BoardDrillRow } from "./boardDrill.d.mts";
import { ExecKpiFetchError, fetchAllPages, isTableMissingMessage } from "./execKpiFetch.mjs";
import { isColumnMissingError } from "./sanityGate";
import { getSupabase } from "./supabase";
import { scopeTenant } from "./tenantScope.mjs";

/**
 * MER-708: the card drill's data access. Thin on purpose: the paging loop is
 * the Executive page's generic fetchAllPages (imported, not changed) and
 * every decision about what the rows mean lives in boardDrill.mjs.
 *
 * ONE READ PER SHEET AND MONTH, not one per click. A sheet's slice is every
 * line's total row plus the customer rows of the two or three lines that
 * carry detail, which is around a hundred rows: one page, fetched when the
 * reader changes sheet or month. Fetching per click would make the cards
 * unmarkable until clicked, and fetching the whole tenant would be eight
 * pages on every board load for detail nobody has asked for yet.
 *
 * Bound to the tenant through scopeTenant (MER-245) on top of the 0071 RLS
 * policy. public.board_drill is CLIENT readable, unlike board_variance: it is
 * the customer's own detail about their own accounts, and it carries no
 * client figure at all.
 *
 * Until 0071 is applied the table does not exist: PostgREST answers PGRST205,
 * the error carries `tableMissing`, and the panel says so rather than showing
 * a database message.
 */

export { ExecKpiFetchError as BoardDrillFetchError };

export interface BoardDrillFetchResult {
  rows: BoardDrillRow[];
  truncated: boolean;
  pages: number;
}

/** Both "table missing" classifiers, the execKpiData.ts precedent. */
function isMissing(message: string): boolean {
  return isTableMissingMessage(message) || isColumnMissingError(message);
}

/**
 * Every board_drill row for one sheet and month, in (client_label, rank)
 * order so a page boundary is stable. Throws the shared fetch error;
 * `tableMissing` is true when the table is not in the schema yet.
 */
export async function fetchBoardDrill(
  tenantId: string,
  sheet: string,
  period: string,
): Promise<BoardDrillFetchResult> {
  const supabase = getSupabase();
  const result = await fetchAllPages<BoardDrillRow>(
    (from, to) =>
      scopeTenant(supabase.from(BOARD_DRILL_TABLE).select(BOARD_DRILL_COLUMNS), tenantId)
        .eq("sheet", sheet)
        .eq("period", period)
        .order("client_label", { ascending: true })
        .order("is_line_total", { ascending: false })
        .order("rank", { ascending: true, nullsFirst: true })
        .order("customer_id", { ascending: true })
        .range(from, to)
        // The select list is a runtime string (pinned against 0071 by
        // scripts/board-drill.test.mjs), so the typed column parser cannot
        // read it.
        .overrideTypes<BoardDrillRow[], { merge: false }>(),
    { pageSize: DRILL_PAGE_SIZE, maxPages: DRILL_MAX_PAGES, isMissing },
  );
  return { rows: result.rows, truncated: result.truncated, pages: result.pages };
}
